import {
  FluidCircleTypes,
  FluidLoadingSpinnerConfig,
  FluidSize,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { parseSizeValue } from '../../../utils/formatting/formatting.utils';
import { Utils } from '../../../utils/utils';
import { LOADING_SPINNER_ID } from '../../fluid-form/util/fluid-form.utils';

// ================================================================ //
// -- Public Functional Components

/**
 * Creates an overlay and wrapper for slotted content and absolutely positions the loader
 * over the top of it, blocking the UI in that section, and showing the spinner.
 * @param props
 * @constructor
 */
export const BlockingLoader: FunctionalComponent<FluidLoadingSpinnerConfig> = (
  props
) => {
  const { height, spinnerId, loading, zIndex } = props;
  const style = height && { minHeight: `${height}px` };
  return (
    <div
      class='loader-container'
      style={style}
      data-fluid-id={LOADING_SPINNER_ID(spinnerId, 'container')}>
      {loading && [
        <div
          class='overlay'
          style={{ 'z-index': `${zIndex}` }}
          data-fluid-id={LOADING_SPINNER_ID(spinnerId, 'overlay')}>
          <LoadingSpinner {...props} />
        </div>,
      ]}
      <slot />
    </div>
  );
};

/**
 * Calculate the correct ARIA properties and render the spinner based on
 * the circle type.
 * @param props
 * @constructor
 */
export const LoadingSpinner: FunctionalComponent<FluidLoadingSpinnerConfig> = (
  props
) => {
  const {
    verticalPadding,
    loading,
    muted,
    spinnerId,
    zIndex,
    loadingMessage,
    size,
    circleType,
    container,
    centered,
  } = props;

  if (!isNullOrUndefined(loading) && loading === false) return;

  const pathClass = {
    path: true,
    muted: muted,
  };

  return (
    <div
      data-fluid-id={LOADING_SPINNER_ID(spinnerId, 'wrap')}
      class={{
        'spinner-container': true,
        [`padding-${verticalPadding}`]: Utils.isFluidEnum<FluidSize>(
          verticalPadding,
          FluidSize
        ),
      }}
      style={{
        'z-index': `${zIndex}`,
        'padding-top': parseSizeValue(verticalPadding),
        'padding-bottom': parseSizeValue(verticalPadding),
      }}>
      <div
        data-fluid-id={LOADING_SPINNER_ID(spinnerId, 'spinner')}
        role='progressbar'
        aria-busy='true'
        aria-valuemin='0'
        aria-valuemax='100'
        aria-valuetext={loadingMessage ? loadingMessage : 'Loading'}
        aria-live={loadingMessage ? 'assertive' : 'polite'}
        class={{
          [`${circleType}-circle circle-${size}`]: true,
          centered: centered || container,
        }}>
        <div class={pathClass} />
        {circleType === FluidCircleTypes.NOTCHED && [
          <div class={pathClass} />,
          <div class={pathClass} />,
          <div class={pathClass} />,
        ]}
      </div>
      <LoadingMessage {...props} />
    </div>
  );
};

/**
 * Renders a loading message if one exists
 * @param props
 * @constructor
 */
export const LoadingMessage: FunctionalComponent<FluidLoadingSpinnerConfig> = (
  props
) => {
  const { loadingMessage, spinnerId, centered } = props;

  if (!loadingMessage) return;

  return (
    <span
      data-fluid-id={LOADING_SPINNER_ID(spinnerId, 'message')}
      class={{
        'loading-message': true,
        centered: centered,
      }}>
      {loadingMessage}
    </span>
  );
};
